import { forwardRef, useRef } from "react";
import Dialog from "./AnimatedDialog";
import OverlayHeading from "./OverlayHeading";
import "./overlaySurfaces.css";
import "./responsiveOverlays.css";

// Drop-in replacement for PrimeReact's Dialog with the portal's responsive default:
// - below 768px it becomes a full-screen sheet (unless `sheet={false}`, used for short confirmations),
// - from 768px up, any width/minWidth it was given is capped at 95vw so it never overflows,
// - between 768px and 1279px a vw-sized dialog is never narrower than min(600px, 95vw).
// See docs/RESPONSIVE.md.
export const capWidth = (value) => {
	if (value === undefined || value === null || value === "auto") return value;
	const css = typeof value === "number" ? `${value}px` : String(value);
	return css.startsWith("min(") ? css : `min(${css}, 95vw)`;
};

const ResponsiveDialog = forwardRef(function ResponsiveDialog(
	{ sheet = true, className = "", style, draggable = false, header, description, icon, maskClassName = "", visible, onExited, ...props },
	ref
) {
	const trigger = useRef(null);
	const open = useRef(false);
	// Capture before children mount: an auto-focused field must not replace the opener.
	if (visible && !open.current) trigger.current = document.activeElement;
	open.current = visible;
	const restoreFocus = (...args) => {
		if (!open.current && !document.querySelector('.p-dialog:not(.p-dialog-exit-active):not(.p-dialog-exit-done), .p-sidebar-active')) trigger.current?.focus?.();
		onExited?.(...args);
	};
	const sized = { ...style };
	if (sized.width !== undefined) sized.width = capWidth(sized.width);
	if (sized.minWidth !== undefined) sized.minWidth = capWidth(sized.minWidth);
	if (sized.maxWidth !== undefined) sized.maxWidth = capWidth(sized.maxWidth);
	const classes = ["akello-responsive-dialog", sheet ? "akello-dialog-sheet" : "", className]
		.filter(Boolean)
		.join(" ");
	const heading = typeof header === "string" ? <OverlayHeading title={header} description={description} icon={icon} /> : header;
	return <Dialog ref={ref} visible={visible} onExited={restoreFocus} draggable={draggable} resizable={false} blockScroll maskClassName={`akello-form-dialog-mask ${maskClassName}`} {...props} header={heading} className={classes} style={sized} />;
});

export default ResponsiveDialog;
